import {useMemo} from 'react';
import type {MessageDescriptor} from 'react-intl';
import {usersApi} from '../../../store/features/Users/userService';
import {teamApi, useGetStatusesQuery} from '../../../store/features/Team/teamService';
import {mergeStatuses} from '../../../store/features/Team/statusesLongPoll';
import {useStatusesLongPoll} from '../../../hooks/useStatusesLongPoll';
import {prepMessages as m} from './prepMessages';

export type PrepPresence = 'available' | 'talking' | 'dnd' | 'offline';

export const PRESENCE_TEXT: Readonly<Record<PrepPresence, MessageDescriptor>> = {
    available: m.presenceAvailable,
    talking: m.presenceTalking,
    dnd: m.presenceSilent,
    offline: m.presenceOffline,
};

export const presenceOf = (status: {telcoUserStatus?: {status?: string} | null; doNotDisturbUserStatus?: {enabled?: boolean} | null} | undefined): PrepPresence => {
    if (status?.doNotDisturbUserStatus?.enabled) {
        return 'dnd';
    }
    const telco = status?.telcoUserStatus?.status;
    if (telco === 'TALKING') {
        return 'talking';
    }
    return !telco || telco === 'OFFLINE' || telco === 'UNKNOWN' ? 'offline' : 'available';
};

export const usePrepPresence = (matrixUserIds: readonly string[]): ReadonlyMap<string, PrepPresence> => {
    const {data: ids} = usersApi.endpoints.resolveMatrixIds.useQuery({matrixIds: [...matrixUserIds]}, {skip: matrixUserIds.length === 0});
    const userIds = useMemo(() => Object.values(ids ?? {}).filter(Boolean).map(Number), [ids]);
    const {data: statuses} = useGetStatusesQuery({users: userIds}, {skip: userIds.length === 0});
    useStatusesLongPoll({
        users: userIds,
        update: (incoming) => teamApi.util.updateQueryData('getStatuses', {users: userIds}, mergeStatuses(incoming)),
        skip: userIds.length === 0,
    });
    return useMemo(() => {
        const byUserId = new Map((statuses ?? []).map(status => [String(status.userId), status]));
        return new Map(matrixUserIds.map(matrixUserId => [matrixUserId, presenceOf(byUserId.get(ids?.[matrixUserId] ?? ''))]));
    }, [ids, matrixUserIds, statuses]);
};
